import { Component, Inject, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../../../core/services/core.translate.service';
import { GfTitleService } from '../../../../core/services/core.title.service';
import { TrackerRest } from '../../services/tracker.rest';
import { TrackerService } from '../../services/tracker.service';
import { UiService } from '../../../../core/services/core.ui.service';
import { TrackerExtraFieldEditComponent } from './trackerExtraFieldEditModal/tracker.extra.field.edit.modal.component';
import { TrackerExtraFieldDeleteComponent } from './trackerExtraFieldDeleteModal/tracker.extra.field.delete.modal.controller';
import * as _ from 'lodash';

@Component({
  selector: 'tracker-admin-fields',
  template: require('./tracker.fields.component.html')
})
export class TrackerAdminFieldsComponent implements OnInit {
  @Input() tracker: any;
  @Input() siteConfig: any;

  isLoading: boolean;
  intrinsicFields: any[];
  extraFields: any[];
  fieldAliasToDelete: string;
  carbonCopyEnabled: boolean;
  statusEnabled: boolean;
  fieldTypeMap: any[];
  selectedField: string;

  constructor(
    private $window: Window,
    private trackerRest: TrackerRest,
    private trackerService: TrackerService,
    private uiService: UiService,
    private modalService: NgbModal,
    private stateService: StateService, 
    private translateService: GfTranslateService,
    private titleService: GfTitleService
  ) {
    this.isLoading = false;
    this.fieldAliasToDelete = '';
    this.selectedField = '';
  }

  /**
   * Initializes the component, setting up initial values and configurations.
   */
  ngOnInit() {
    const COUNT_INTRINSIC_FIELDS = 6;
    let fields = this.getExtraFields(_.get(this.tracker, 'fields', []));
    this.intrinsicFields = this.filterIntrinsicFields(fields);
    this.extraFields = this.filterExtraFields(fields);

    this.carbonCopyEnabled = this.siteConfig.trackerCCEnabled !== '';
    this.statusEnabled = false;
    this.fieldTypeMap = [
      { type: 4, glyph: 'glyphicon-align-justify', title: this.translateService.translate('Text Box'), active: true },
      { type: 6, glyph: 'glyphicon-align-justify', title: this.translateService.translate('Text Area'), active: true },
      { type: 1, glyph: 'glyphicon-list-alt', title: this.translateService.translate('Select'), active: true },
      { type: 5, glyph: 'glyphicon-list-alt', title: this.translateService.translate('Multiselect'), active: true },
      { type: 3, glyph: 'glyphicon-record', title: this.translateService.translate('Option Buttons'), active: true },
      { type: 2, glyph: 'glyphicon-unchecked', title: this.translateService.translate('Checkbox'), active: true },
      { type: 9, glyph: 'glyphicon-calendar', title: this.translateService.translate('Date'), active: true },
      { type: 10, glyph: 'glyphicon-stats', title: this.translateService.translate('Numeric'), active: true },
      { type: 11, glyph: 'glyphicon-barcode', title: this.translateService.translate('Serial'), active: true },
      { type: 7, glyph: 'glyphicon-list-alt', title: this.translateService.translate('Status'), active: this.statusEnabled },
      { type: 12, glyph: 'glyphicon-user', title: this.translateService.translate('Members'), active: true },
      { type: 8, glyph: 'glyphicon-download-alt', title: this.translateService.translate('Release'), active: true },
      { type: 13, glyph: 'glyphicon-envelope', title: this.translateService.translate('Carbon Copy'), active: this.carbonCopyEnabled }
    ];
    this.titleService.setTitle(this.tracker.trackerName, 'Admin');
  }

  /**
   * Opens the delete modal for a given field.
   * @param field The field to delete.
   */
  showDelete(field: any) {
    if (!field) {
      return;
    }

    const modalRef = this.modalService.open(TrackerExtraFieldDeleteComponent);
    modalRef.componentInstance.field = field;
    modalRef.componentInstance.close.subscribe((field) => {
      if (_.isObject(field)) {
        this.doDelete(_.get(field, 'id'));
      }

      modalRef.close();
    });
  }

  /**
   * Deletes the specified field.
   * @param tefId The ID of the field to delete.
   */
  doDelete(tefId: number) {
    this.isLoading = true;
    const delCompleteMsg = this.translateService.translate('Delete complete');
    const delFailedMsg = this.translateService.translate('Delete Failed');

    this.trackerRest.tefDelete(this.tracker.id, tefId).then(
      () => {
        this.uiService.message.showSuccess(`Field ${tefId} in tracker ${this.tracker.trackerName} deleted`, delCompleteMsg);
        this.reload();
      },
      response => {
        this.uiService.message.showError(response, delFailedMsg);
        this.isLoading = false;
      }
    );
  }

  /**
   * Moves a field up in the order.
   * @param field The field to move up.
   */
  moveUp(field: any) {
    if (!field.isExtraField || field.fieldOrder < 1) return;

    // Find the object that has the next lower fieldOrder value
    const lowerFields = this.extraFields.filter(f => f.fieldOrder < field.fieldOrder);

    if (lowerFields.length === 0) return; // No lower fieldOrder values exist, so return

    const toSwitch = lowerFields.reduce((prev, curr) => {
        // Find the field with the highest fieldOrder among those lower than the current field
        return (prev.fieldOrder > curr.fieldOrder) ? prev : curr;
    });

    if (!toSwitch) return; // Safeguard: in case reduce somehow fails to find a field

    // Swap the fieldOrder values
    const tempOrder = field.fieldOrder;
    field.fieldOrder = toSwitch.fieldOrder;
    toSwitch.fieldOrder = tempOrder;

    // Persist the new order
    this.persistExtraFieldsOrder();
  }

  /**
   * Moves a field down in the order.
   * @param field The field to move down.
   */
  moveDown(field: any) {
    if (!field.isExtraField) return;

    // Find the object that has the next higher fieldOrder value
    const higherFields = this.extraFields.filter(f => f.fieldOrder > field.fieldOrder);

    if (higherFields.length === 0) return; // No higher fieldOrder values exist, so return

    const toSwitch = higherFields.reduce((prev, curr) => {
        // Find the field with the lowest fieldOrder among those higher than the current field
        return (prev.fieldOrder < curr.fieldOrder) ? prev : curr;
    });

    if (!toSwitch) return; // Safeguard: in case reduce somehow fails to find a field

    // Swap the fieldOrder values
    const tempOrder = field.fieldOrder;
    field.fieldOrder = toSwitch.fieldOrder;
    toSwitch.fieldOrder = tempOrder;

    // Persist the new order
    this.persistExtraFieldsOrder();
  }

  /**
   * Toggles a setting for a field.
   * @param field The field to update.
   * @param settingName The name of the setting to toggle.
   */
  toggleSetting(field: any, settingName: string) {
    this.isLoading = true;
    field[settingName] = !field[settingName];

    if (settingName === 'isEnabled' && !field[settingName]) {
      field['isRequired'] = false;
      field['showOnSubmit'] = false;
    } else if (settingName === 'isRequired' && field[settingName]) {
      field['isEnabled'] = true;
      field['showOnSubmit'] = true;
    } else if (settingName === 'showOnSubmit' && field[settingName]) {
      field['isEnabled'] = true;
    }

    const _field = {
      id: field.id,
      alias: field.alias,
      fieldName: field.fieldName,
      isEnabled: field.isEnabled,
      isRequired: field.isRequired,
      showOnSubmit: field.showOnSubmit
    };

    this.persistExtraField(_field)
      .then(() => {
        this.uiService.message.showSuccess(
          this.translateService.translate('Updated setting {{fieldName}} ({{alias}})', _field),
          this.translateService.translate('Edit Setting')
        );
      })
      .catch(response => {
        this.uiService.message.showError(
          this.translateService.translate('Could not update setting {{fieldName}} ({{alias}})', field),
          this.translateService.translate('Edit Setting')
        );
      })
      .finally(() => {
        this.reload();
      });
  }

  /**
   * Gets the description of a field type.
   * @param id The ID of the field type.
   * @returns The description of the field type.
   */
  getTypeDescription(id: number) {
    return this.trackerService.fields.types.getDescription(id);
  }

  /**
   * Shows the modal to add a new extra field.
   * @param fieldType The type of the new field.
   */
  showAddTef(fieldType: number) {
    let fieldOrder = 99999;
    const maxFieldOrderTef = _.maxBy(this.extraFields, extraField => extraField.fieldOrder);

    if (!_.isEmpty(maxFieldOrderTef)) {
      fieldOrder = maxFieldOrderTef.fieldOrder + 1;
    }

    const field: any = {
      alias: '',
      fieldName: '',
      fieldType,
      isRequired: false,
      fieldOrder,
      useInQuickBrowse: false,
      showOnSubmit: true,
      defaultValue: '',
      fieldElements: [],
      isExtraField: true,
      isEnabled: true,
      useWorkflow: false,
      attribute1: '',
      attribute2: ''
    };

    switch (fieldType) {
      case 4:
      case 10:
      case 11:
        field.attribute2 = 25;
        break;
      case 6:
        field.attribute1 = 25;
        break;
      case 8:
        const _fields = _.filter(this.extraFields, { attribute2: true });
        field.attribute2 = _fields.length === 0 ? true : false;
        break;
    }

    this.editTef(field);
  }

  /**
   * Opens the edit modal for a given field.
   * @param field The field to edit.
   */
  editTef(field: any) {
    this.selectedField = field.alias;

    const modalRef = this.modalService.open(TrackerExtraFieldEditComponent);
    modalRef.componentInstance.field = field;
    modalRef.componentInstance.fieldTypeMap = this.fieldTypeMap;
    modalRef.componentInstance.close.subscribe((field) => {
      modalRef.close();

      if (_.isEmpty(field) || !_.has(field, 'fieldName')) {
        return;
      }

      if (field.alias === 'storyPoints') {
        this.tracker.storyPointsList = {
          list: _.map(field.fieldElements, element => element.id),
          default: field.defaultValue
        };

        this.trackerService.save(this.tracker)
          .then(() => {
            this.saveFieldSettings(field);
          })
          .catch(() => {
            this.uiService.message.showError(
              this.translateService.translate('Could not update {{fieldName}} ({{alias}})', field),
              this.translateService.translate('Edit Field')
            );
          });
      } else {
        this.saveFieldSettings(field);
      }
    });
  }

  /**
   * Filters intrinsic fields from the given fields.
   * @param fields The fields to filter.
   * @returns The filtered intrinsic fields.
   */
  filterIntrinsicFields(fields: any) {
    return _.filter(fields, (field) => { return (!field.isExtraField || field.fieldType === 7) });
  }

  /**
   * Filters extra fields from the given fields.
   * @param fields The fields to filter.
   * @returns The filtered extra fields.
   */
  filterExtraFields(fields: any) {
    return _.filter(fields, (field) => { return (field.isExtraField && field.fieldType !== 7) });
  }

  /**
   * Gets the glyph icon for a field type.
   * @param field_type The type of the field.
   * @returns The glyph icon for the field type.
   */
  getFieldTypeGlyphIcon(field_type: any) {
    const field = _.find(this.fieldTypeMap, { type: parseInt(field_type, 10) });
    return field ? field.glyph : '';
  }

  /**
   * Gets extra fields from the given fields.
   * @param fields The fields to filter.
   * @returns The filtered extra fields.
   */
  getExtraFields(fields: any) {
    const remapAttr = item => {
      if (item.fieldType === 8) {
        item.attribute2 = item.attribute2 === 1;
      }
      return item;
    };

    const withoutOpenClose = item => {
      if (item.isExtraField) return true;
      return !['openDate', 'closeDate'].includes(item.alias);
    };

    const list = _.sortBy(
      _.map(fields, remapAttr).filter(withoutOpenClose),
      'fieldOrder'
    );

    let idx = 0;
    return list.map(item => {
      item.fieldOrder = item.isExtraField ? idx++ : 0;
      return item;
    });
  }

  /**
   * Persists the order of extra fields.
   * @returns A promise that resolves when the order is persisted.
   */
  persistExtraFieldsOrder() {
    return this.trackerRest.tefPersist(this.tracker.id, this.extraFields)
      .then(() => {
        this.uiService.message.showSuccess('Field order change updated');
      })
      .catch(() => {
        this.uiService.message.showError(this.translateService.translate('There was an error updating the fields order'));
      });
  }

  /**
   * Validates a release field.
   * @param field The field to validate.
   * @returns True if the field is valid, false otherwise.
   */
  validateReleaseField(field: any) {
    const _fields = _.filter(this.extraFields, extraField => extraField.attribute2 === true && field.attribute2 === true && extraField.alias !== field.alias);
    field.attribute2 = field.attribute2 ? 1 : 0;
    field.attribute1 = 0;

    if (_fields.length > 0) {
      this.uiService.message.showError(this.translateService.translate('Cannot update field, there is already a release field bound to the roadmap'));
      return false;
    }

    return true;
  }

  /**
   * Validates a field.
   * @param field The field to validate.
   * @returns True if the field is valid, false otherwise.
   */
  validateField(field: any) {
    let res;
    switch (field.fieldType) {
      case 8:
        res = this.validateReleaseField(field);
        break;
      default:
        res = true;
        break;
    }

    return res;
  }

  /**
   * Persists an extra field.
   * @param field The field to persist.
   * @returns A promise that resolves when the field is persisted.
   */
  persistExtraField(field: any) {
    return new Promise((resolve, reject) => {
      const _field = _.cloneDeep(field);

      if (!this.validateField(_field)) {
        setTimeout(() => {
          reject();
        });
      } else {
        this.trackerRest.tefPersist(this.tracker.id, { fields: [_field] })
          .then(response => {
            resolve(field);
          })
          .catch(response => {
            reject(response);
          });
      }
    });
  }

  /**
   * Saves the settings for a field.
   * @param field The field to save.
   */
  saveFieldSettings(field: any) {
    const isUpdate = field.id > 0;

    this.persistExtraField(field)
      .then(() => {
        const params = {
          alias: field.alias,
          fieldName: field.fieldName,
          operation: isUpdate ? this.translateService.translate('Updated') : this.translateService.translate('Added')
        };

        this.uiService.message.showSuccess(
          this.translateService.translate('{{operation}} {{fieldName}} ({{alias}})', params),
          this.translateService.translate('{{operation}} Field', {
            operation: isUpdate ? this.translateService.translate('Edit') : this.translateService.translate('Add')
          })
        );

        this.reload();
      })
      .catch(response => {
        if (response && response.status === 400 && response.data) {
          const inUse = _.find(_.get(response, 'data.elements', []), { type: 'in-use' });

          if (inUse) {
            const data = {
              fieldName: field.fieldName,
              alias: field.alias,
              elements: _.map(_.get(inUse, 'args', []), item => item.elementName).join(', ')
            };
            this.uiService.message.showError(
              this.translateService.translate('Cannot delete {{elements}} from {{fieldName}} ({{alias}}).  They are in use by existing Tracker Item(s).  Change the status of affected TIs and try again.', data),
              this.translateService.translate('Delete Element')
            );
          } else {
            const errorList = [];
            _.each(response.data, errors => {
              for (const field_name in errors) {
                _.each(errors[field_name], error => {
                  errorList.push(`${error.type} in ${field_name}`);
                });
              }
            });

            this.uiService.message.showError(
              this.translateService.translate('Could not {{operation}} the field. Check ' + errorList.join(', ')),
              this.translateService.translate('{{operation}}', {
                operation: isUpdate ? this.translateService.translate('Edit') : this.translateService.translate('Add')
              })
            );
          }
        } else {
          const params = {
            alias: field.alias,
            fieldName: field.fieldName,
            operation: isUpdate ? this.translateService.translate('Updated') : this.translateService.translate('Added')
          };

          this.uiService.message.showError(
            this.translateService.translate('Could not {{operation}} {{fieldName}} ({{alias}})', params),
            this.translateService.translate('{{operation}} Field', {
              operation: isUpdate ? this.translateService.translate('Edit') : this.translateService.translate('Add')
            })
          );
        }
      })
      .finally(() => {
        this.selectedField = '';
      });
  }

  /**
   * Reloads the state.
   */
  reload() {
    const currentTab = _.get(this.stateService, 'params.tab', 'tab-fields');
    const params = { ...this.stateService.params, tab: currentTab };
    this.stateService.go('.', params, { reload: true, inherit: true, notify: true });
  }
}
